import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import type { FileRejection } from "react-dropzone";

import { toastError } from "@alpacahq/ui/old";

import {
  deleteFileUpload,
  type OwnerDocument,
} from "~/v2/api/rest/entityOnboarding";
import { useGetID } from "./useGetID";

export type ExistingID = {
  id: string;
  name: string;
};

export type IDType =
  | "drivers_license"
  | "passport"
  | "national_id_card"
  | "residence_permit";

export type ImageSide = "front" | "back";
export type ImageRequirement = ImageSide[];

export const ID_TYPES: Record<
  IDType,
  { label: string; value: IDType; imageRequirement: ImageRequirement }
> = {
  drivers_license: {
    label: "Driver's license",
    value: "drivers_license",
    imageRequirement: ["front", "back"],
  },
  passport: {
    label: "Passport",
    value: "passport",
    imageRequirement: ["front"],
  },
  national_id_card: {
    label: "National Identity Card",
    value: "national_id_card",
    imageRequirement: ["front", "back"],
  },
  residence_permit: {
    label: "Residence Permit",
    value: "residence_permit",
    imageRequirement: ["front", "back"],
  },
};

export interface UseUploadIDArgs {
  accountId: string;
  ownerId: string;
}

export const useUploadID = ({ accountId, ownerId }: UseUploadIDArgs) => {
  const existingId = useGetID(accountId, ownerId);

  const [idType, setIDType] = useState<IDType | undefined>(undefined);

  const [id, setId] = useState<{
    front: File | ExistingID | null;
    back: File | ExistingID | null;
  }>({
    front: existingId?.passport
      ? { id: existingId.passport.id, name: "passport" }
      : existingId?.front
        ? { id: existingId.front.id, name: "front_id" }
        : null,
    // if passport exists, don't add back
    back: existingId?.passport
      ? null
      : existingId?.back
        ? { id: existingId.back.id, name: "back_id" }
        : null,
  });

  const [errors, setErrors] = useState<{
    front: string | null;
    back: string | null;
  }>({ front: null, back: null });

  const deleteMutation = useMutation({
    mutationFn: (side: ImageSide) =>
      deleteFileUpload({
        accountId,
        ownerId,
        documentId:
          side === "front" && existingId.passport
            ? (existingId.passport as OwnerDocument).id
            : (existingId[side] as OwnerDocument).id,
      }),
    onSuccess: () => {
      existingId.refetch();
    },
    onError: (error) => {
      console.error(error);
      toastError("Failed to delete ID image.");
    },
  });

  const onUpload = (file: File | ExistingID, side: ImageSide) => {
    setId((prev) => ({ ...prev, [side]: file }));
  };

  const onError = (file: FileRejection, side: ImageSide) => {
    setId((prev) => ({
      ...prev,
      [side]: null,
    }));
    setErrors((prev) => ({
      ...prev,
      [side]: file.errors.map((err) => err.message).join(", "),
    }));
  };

  const onRemove = (side: ImageSide) => {
    setId((prev) => ({
      ...prev,
      [side]: null,
    }));
    setErrors((prev) => ({
      ...prev,
      [side]: null,
    }));
    deleteMutation.mutate(side);
  };

  const setError = (side: ImageSide, error: string | null) => {
    setErrors((prev) => ({
      ...prev,
      [side]: error,
    }));
  };

  const imageRequirement = idType
    ? ID_TYPES[idType]?.imageRequirement
    : ["front", "back"];

  // reset when existingID updates
  // biome-ignore lint/correctness/useExhaustiveDependencies: accountId and ownerId are triggers: the inferred ID type is recomputed when the owner being uploaded for changes. The body only writes them.
  useEffect(() => {
    // we only really know if they have a passport, otherwise just guess drivers license
    setIDType(
      existingId.passport
        ? "passport"
        : existingId.front && existingId.back
          ? "drivers_license"
          : undefined,
    );
    setErrors({ front: null, back: null });
    setId({
      front: existingId?.passport
        ? { id: existingId.passport.id, name: "passport" }
        : existingId?.front
          ? { id: existingId.front.id, name: "front_id" }
          : null,
      back: existingId?.back
        ? { id: existingId.back.id, name: "back_id" }
        : null,
    });
  }, [
    accountId,
    ownerId,
    existingId.passport,
    existingId.front,
    existingId.back,
  ]);

  return {
    id,
    idType,
    imageRequirement,
    setIDType,
    onUpload,
    onError,
    onRemove,
    errors,
    setError,
  };
};

export type UseUploadIDReturn = ReturnType<typeof useUploadID>;
